export type Service = {
    slug: string
    index: string
    name: string
    promise: string
    description: string
    deliverables: string[]
    timeline: string
    image?: string
    imageAlt?: string
    visual?: 'evaluation' | 'dashboard'
}

export type Engagement = {
    slug: string
    name: string
    duration: string
    summary: string
    includes: string[]
    featured?: boolean
}

export type Faq = {
    question: string
    answer: string
}

export const SERVICES: Service[] = [
    {
        slug: 'ai-strategy',
        index: '01',
        name: 'AI Strategy',
        promise: 'Know exactly where AI will pay off.',
        description: 'We assess your operations, data and systems, identify the opportunities with the highest return and build a prioritized roadmap with a clear business case for each one.',
        deliverables: [
            'Opportunity assessment',
            'Prioritized automation roadmap',
            'Business cases and ROI model',
            'Executive briefing'
        ],
        timeline: '2 to 4 weeks',
        image: '/images/strategy-workshop.jpg',
        imageAlt: 'Workshop room prepared for a strategy session'
    },
    {
        slug: 'automation-consulting',
        index: '02',
        name: 'Automation Consulting',
        promise: 'Senior guidance for teams building in-house.',
        description: 'Experienced advisors who help your teams choose the right tools, design robust automations and put the governance in place that keeps them safe at scale.',
        deliverables: [
            'Architecture reviews',
            'Tooling recommendations',
            'Governance framework',
            'Team enablement sessions'
        ],
        timeline: 'Flexible retainer',
        image: '/images/arch-stairs.jpg',
        imageAlt: 'Light falling across a minimal staircase'
    },
    {
        slug: 'ai-agent-development',
        index: '03',
        name: 'AI Agent Development',
        promise: 'Custom agents, built for your work.',
        description: 'We design, build and evaluate agents trained on your processes and knowledge, with guardrails, human checkpoints and performance testing before anything goes live.',
        deliverables: [
            'Production-ready agents',
            'Evaluation suite on real cases',
            'Guardrails and escalation rules',
            'Usage and quality analytics'
        ],
        timeline: '4 to 8 weeks',
        visual: 'evaluation'
    },
    {
        slug: 'workflow-design',
        index: '04',
        name: 'Workflow Design',
        promise: 'Processes redesigned around automation.',
        description: 'We map how work really flows today, remove the steps that add nothing and design automated workflows with clear ownership, approvals and exception handling.',
        deliverables: [
            'Current state process maps',
            'Future state workflow design',
            'Automation specifications',
            'Change and adoption plan'
        ],
        timeline: '2 to 3 weeks',
        image: '/images/arch-curve.jpg',
        imageAlt: 'Flowing curved architecture against a clear sky'
    },
    {
        slug: 'system-integration',
        index: '05',
        name: 'System Integration',
        promise: 'Every tool connected, every record in sync.',
        description: 'We connect agents and workflows to your CRM, ERP, helpdesk, data warehouse and internal tools through secure, monitored integrations that your IT team can trust.',
        deliverables: [
            'API and data integrations',
            'Single sign-on and permissions',
            'Data pipelines',
            'Integration monitoring and alerts'
        ],
        timeline: '3 to 6 weeks',
        image: '/images/case-finance.jpg',
        imageAlt: 'City towers connected above the clouds'
    },
    {
        slug: 'deployment-optimization',
        index: '06',
        name: 'Deployment & Optimization',
        promise: 'Live, measured and improving every month.',
        description: 'We roll out in stages, train your people, monitor performance in production and keep tuning, so results keep growing long after launch day.',
        deliverables: [
            'Staged rollout plan',
            'Team training and playbooks',
            'Live performance dashboard',
            'Monthly optimization reviews'
        ],
        timeline: 'Ongoing',
        visual: 'dashboard'
    }
]

export const ENGAGEMENTS: Engagement[] = [
    {
        slug: 'discovery-sprint',
        name: 'Discovery Sprint',
        duration: '2 weeks',
        summary: 'A focused assessment of your operations that ends with a prioritized roadmap and a business case for your first automation.',
        includes: [
            'Stakeholder interviews and process mapping',
            'Opportunity scorecard',
            'Roadmap and ROI model'
        ]
    },
    {
        slug: 'build-partnership',
        name: 'Build Partnership',
        duration: '8 to 12 weeks',
        summary: 'We design and ship your first production systems alongside your team, from architecture through rollout and training.',
        includes: [
            'Solution architecture',
            'Agents, workflows and integrations',
            'Rollout, training and handover'
        ],
        featured: true
    },
    {
        slug: 'managed-intelligence',
        name: 'Managed Intelligence',
        duration: 'Ongoing',
        summary: 'We run, monitor and expand your automation portfolio as a long-term partner, with clear monthly reporting on impact.',
        includes: [
            'Monitoring and incident response',
            'Continuous tuning and retraining',
            'Quarterly expansion roadmap'
        ]
    }
]

export const SERVICE_FAQS: Faq[] = [
    {
        question: 'How soon will we see an impact?',
        answer: 'Most clients have a first system in production within six weeks of kickoff, and see measurable impact within the first month after launch.'
    },
    {
        question: 'Do we need clean data or a data team?',
        answer: 'No. Part of our work is connecting to the data you already have and making it usable. If something important is missing, we tell you early and plan around it.'
    },
    {
        question: 'Do we have to replace any of our current software?',
        answer: 'No. We connect to the CRMs, ERPs, helpdesks, data warehouses and internal systems you already run, and build custom connectors when something has no ready-made link.'
    },
    {
        question: 'How do you keep our data secure?',
        answer: 'Systems run in your environment or a dedicated private one, with single sign-on, role-based permissions, encryption and full audit logs. NDAs and data processing agreements are standard.'
    },
    {
        question: 'What happens after launch?',
        answer: 'You own everything we build. We can hand over completely with documentation and training, or stay on to monitor, optimize and extend automation to new processes.'
    },
    {
        question: 'How is pricing structured?',
        answer: 'Strategy and build work is scoped as fixed engagements, and ongoing optimization runs on a monthly retainer. You receive a clear proposal after the first consultation.'
    }
]
